import {
  ChecklistFieldDef,
  ChecklistRepeatableGroupDef,
  ChecklistSectionDef,
  emptyRepeatableRow,
  repeatableEntriesKey,
  sectionFlowMode,
  normalizeDeedIdForSelect,
} from "../components/DynamicChecklistManagement/DynamicChecklistManagement";

/** EC verification hierarchy: document block → entry lines. */
export type EcHierarchyLevel = "ec_block" | "ec_entry";

export const EC_BLOCK_GROUP_ID = "rg_ec_block";
export const EC_ENTRY_GROUP_ID = "rg_ec_entry";

export const EC_HIERARCHY_LEVELS: {
  key: EcHierarchyLevel;
  label: string;
  subtitle: string;
}[] = [
  {
    key: "ec_block",
    label: "EC document details",
    subtitle: "Date, SRO, EC number, entries count, period, sheets",
  },
  {
    key: "ec_entry",
    label: "Entry details",
    subtitle: "Per-line date, document, deed, favour, executant, remark",
  },
];

export const EC_HIERARCHY_META = {
  BLOCK_ID: "__ec_block_id__",
  BLOCK_POS: "__ec_block_pos__",
  FOD_FK: "__ec_fod_fk__",
  LINE_INDEX: "__ec_line_index__",
  RELATED: "__ec_related__",
  DATE_ID: "__ec_date_id__",
} as const;

export interface EcVerificationLineRow {
  line_index: number;
  date_id?: number;
  date?: string;
  doc?: string;
  doc_text?: string;
  deed?: string | number;
  deed_name?: string;
  favour?: string;
  executant?: string;
  extent?: string;
  is_other_doc?: boolean;
  other_doc?: string;
  related?: boolean;
  remark?: string;
}

export interface EcVerificationBlockRow {
  ec_block_id: number;
  fod_fk?: number;
  position?: number;
  date?: string;
  entry_date?: string;
  sale_date?: string;
  sro?: string;
  sro_names?: string;
  ec_no?: string;
  ec_entries?: string | number;
  org_pres?: string;
  totalSheetCount?: string | number;
  availSheetCount?: string | number;
  missingSheetCount?: string;
  remark?: string;
  lines?: EcVerificationLineRow[];
  /** ec_get.php-compatible entry list (same rows as `lines`). */
  date_list?: EcVerificationDateListRow[];
}

/** Shape returned by ec_get.php `date_list` entries. */
export interface EcVerificationDateListRow {
  date_id?: number | string;
  date?: string;
  doc?: string;
  deed?: string | number;
  favour?: string;
  executant?: string;
  extent?: string;
  is_other_doc?: boolean;
  not_related?: boolean;
  otherDoc?: string;
}

export const EC_VERIFICATION_DYNAMIC_TAGS: { id: string; name: string }[] = [
  { id: "ec_date", name: "ec_date — EC Date" },
  { id: "ec_sro", name: "ec_sro — SRO (names)" },
  { id: "ec_no", name: "ec_no — EC Number" },
  { id: "ec_entries", name: "ec_entries — EC Entries count" },
  { id: "ec_from_date", name: "ec_from_date — From Date" },
  { id: "ec_to_date", name: "ec_to_date — To Date" },
  { id: "ec_org_pres", name: "ec_org_pres — Original present" },
  { id: "ec_total_sheets", name: "ec_total_sheets — Total sheets" },
  { id: "ec_avail_sheets", name: "ec_avail_sheets — Available sheets" },
  { id: "ec_missing_sheets", name: "ec_missing_sheets — Missing sheets / remarks" },
  { id: "ec_remark", name: "ec_remark — EC remark" },
  { id: "ec_entry_date", name: "ec_entry_date — Entry date" },
  { id: "ec_entry_doc", name: "ec_entry_doc — Entry document number" },
  { id: "ec_entry_deed", name: "ec_entry_deed — Entry deed" },
  { id: "ec_entry_favour", name: "ec_entry_favour — Entry favour" },
  { id: "ec_entry_executant", name: "ec_entry_executant — Entry executant" },
  { id: "ec_entry_extent", name: "ec_entry_extent — Entry extent" },
  { id: "ec_entry_other_doc", name: "ec_entry_other_doc — Other document" },
  { id: "ec_entry_remark", name: "ec_entry_remark — Entry remark" },
];

export const EC_TAG_TO_ROW_KEY: Record<string, string> = {
  ec_date: "date",
  ec_sro: "sro_names",
  ec_no: "ec_no",
  ec_entries: "ec_entries",
  ec_from_date: "sale_date",
  ec_to_date: "entry_date",
  ec_org_pres: "org_pres",
  ec_total_sheets: "totalSheetCount",
  ec_avail_sheets: "availSheetCount",
  ec_missing_sheets: "missingSheetCount",
  ec_remark: "remark",
  ec_entry_date: "date",
  ec_entry_doc: "doc_text",
  ec_entry_deed: "deed_name",
  ec_entry_favour: "favour",
  ec_entry_executant: "executant",
  ec_entry_extent: "extent",
  ec_entry_other_doc: "other_doc",
  ec_entry_remark: "remark",
};

function strVal(v: unknown): string {
  return v == null ? "" : String(v).trim();
}

export function ecHierarchyGroupId(level: EcHierarchyLevel): string {
  return level === "ec_block" ? EC_BLOCK_GROUP_ID : EC_ENTRY_GROUP_ID;
}

export function ecLinkedGroups(section: ChecklistSectionDef): ChecklistRepeatableGroupDef[] {
  return (section.repeatableGroups || []).filter((g) => g.ecHierarchyLevel != null);
}

export function ecBlockGroup(section: ChecklistSectionDef): ChecklistRepeatableGroupDef | undefined {
  return ecLinkedGroups(section).find((g) => g.ecHierarchyLevel === "ec_block");
}

export function ecEntryGroup(section: ChecklistSectionDef): ChecklistRepeatableGroupDef | undefined {
  return ecLinkedGroups(section).find((g) => g.ecHierarchyLevel === "ec_entry");
}

export function ecSectionFieldCount(section: ChecklistSectionDef): number {
  const block = ecBlockGroup(section);
  const entry = ecEntryGroup(section);
  const groupFields = (block?.fields.length ?? 0) + (entry?.fields.length ?? 0);
  const ecGroupFieldIds = new Set(
    ecLinkedGroups(section).flatMap((g) => g.fields.map((f) => f.id))
  );
  const standalone = section.fields.filter((f) => !ecGroupFieldIds.has(f.id)).length;
  return standalone + groupFields;
}

/** Ensure EC block + entry detail groups exist when flow is ec_verification. */
export function ensureEcVerificationGroups(section: ChecklistSectionDef): ChecklistSectionDef {
  let groups = [...(section.repeatableGroups || [])];
  const existingLevels = new Set(
    groups.map((g) => g.ecHierarchyLevel).filter((l): l is EcHierarchyLevel => l != null)
  );

  EC_HIERARCHY_LEVELS.forEach((level, idx) => {
    if (existingLevels.has(level.key)) return;
    const groupId = ecHierarchyGroupId(level.key);
    const rgOrder = groups.length ? Math.max(...groups.map((g) => g.order)) + 1 : idx + 1;
    groups.push({
      id: groupId,
      label: level.label,
      order: rgOrder,
      addButtonLabel: level.key === "ec_block" ? "Add EC" : "Add entry",
      minInstances: 0,
      maxInstances: level.key === "ec_block" ? 99 : 999,
      ecHierarchyLevel: level.key,
      fields: [],
    });
  });

  return {
    ...section,
    flowMode: "ec_verification",
    repeatableGroups: groups,
  };
}

export function ecBlocksFromScrutiny(scrutinyApiData: any): EcVerificationBlockRow[] {
  const raw = scrutinyApiData?.ecVerification?.blocks;
  if (!Array.isArray(raw)) return [];
  return raw.map((block) => ({
    ...(block as EcVerificationBlockRow),
    lines: ecLinesFromBlock(block as EcVerificationBlockRow),
  }));
}

/** Lines marked related in EC form (DB `related` = 0, same as ec_get.php). */
export function ecRelatedLinesFromBlock(block: EcVerificationBlockRow): EcVerificationLineRow[] {
  return ecLinesFromBlock(block).filter((line) => isEcLineRelated(line));
}

export function isEcLineRelated(line: EcVerificationLineRow | EcVerificationDateListRow): boolean {
  if ("related" in line && line.related === true) return true;
  if ("not_related" in line && line.not_related === true) return true;
  return false;
}

/** Normalize EC entry lines from `lines` or ec_get-style `date_list`. */
export function ecLinesFromBlock(block: EcVerificationBlockRow): EcVerificationLineRow[] {
  if (Array.isArray(block.lines) && block.lines.length) {
    return block.lines.map((line) => ({
      ...line,
      related: isEcLineRelated(line),
    }));
  }
  const dateList = block.date_list;
  if (!Array.isArray(dateList) || !dateList.length) return [];
  return dateList.map((row, idx) => {
    const isOtherDoc = row.is_other_doc === true;
    const docRaw = strVal(row.doc);
    // ec_get.php: not_related=true means entry is related in EC form UI.
    const related = row.not_related === true;
    const docText = isOtherDoc ? strVal(row.otherDoc) || docRaw : docRaw;
    return {
      line_index: idx + 1,
      date_id: row.date_id != null ? Number(row.date_id) : undefined,
      date: strVal(row.date),
      doc: docRaw,
      doc_text: docText,
      deed: row.deed,
      deed_name: strVal(row.deed),
      favour: strVal(row.favour),
      executant: strVal(row.executant),
      extent: strVal(row.extent),
      is_other_doc: isOtherDoc,
      other_doc: isOtherDoc ? strVal(row.otherDoc) || docRaw : "",
      related,
      remark: "",
    };
  });
}

function fieldTag(field: ChecklistFieldDef): string {
  return normalizeDeedIdForSelect(field.scrutinyLinkedDeedId);
}

function findFieldByTag(
  fields: ChecklistFieldDef[],
  tag: string
): ChecklistFieldDef | undefined {
  return fields.find((f) => fieldTag(f) === tag);
}

function mapTagToSourceRow(tag: string, source: Record<string, string>): string {
  const rowKey = EC_TAG_TO_ROW_KEY[tag];
  if (!rowKey) return "";
  return strVal(source[rowKey]);
}

function prefillFieldIfEmpty(
  row: Record<string, string>,
  field: ChecklistFieldDef,
  source: Record<string, string>
): void {
  const existing = strVal(row[field.id]);
  if (existing) return;
  prefillFieldFromSource(row, field, source);
}

function inferEcEntryTagFromLabel(label: string | undefined): string | null {
  const norm = String(label ?? "")
    .trim()
    .toLowerCase();
  if (!norm) return null;
  if (norm === "date") return "ec_entry_date";
  if (norm.includes("document number") || norm === "doc no" || norm === "doc no.") {
    return "ec_entry_doc";
  }
  if (norm.includes("other document")) return "ec_entry_other_doc";
  if (norm.includes("deed")) return "ec_entry_deed";
  if (norm.includes("favour") || norm.includes("favor") || norm.includes("claimant")) {
    return "ec_entry_favour";
  }
  if (norm.includes("executant")) return "ec_entry_executant";
  if (norm.includes("extent")) return "ec_entry_extent";
  if (norm.includes("remark")) return "ec_entry_remark";
  return null;
}

function resolveEcFieldTag(field: ChecklistFieldDef): string {
  const mapped = fieldTag(field);
  if (mapped) return mapped;
  return inferEcEntryTagFromLabel(field.label) ?? "";
}

function prefillFieldFromSource(
  row: Record<string, string>,
  field: ChecklistFieldDef,
  source: Record<string, string>
): void {
  const tag = resolveEcFieldTag(field);
  if (!tag) return;
  const val = mapTagToSourceRow(tag, source);
  if (val) row[field.id] = val;
}

function normalizeEcBlockSource(block: EcVerificationBlockRow): Record<string, string> {
  return {
    date: strVal(block.date),
    sro_names: strVal(block.sro_names ?? block.sro),
    ec_no: strVal(block.ec_no),
    ec_entries: strVal(block.ec_entries),
    sale_date: strVal(block.sale_date),
    entry_date: strVal(block.entry_date),
    org_pres: strVal(block.org_pres),
    totalSheetCount: strVal(block.totalSheetCount),
    availSheetCount: strVal(block.availSheetCount),
    missingSheetCount: strVal(block.missingSheetCount),
    remark: strVal(block.remark),
    [EC_HIERARCHY_META.BLOCK_ID]: strVal(block.ec_block_id),
    [EC_HIERARCHY_META.BLOCK_POS]: strVal(block.position),
    [EC_HIERARCHY_META.FOD_FK]: strVal(block.fod_fk),
  };
}

function normalizeEcLineSource(line: EcVerificationLineRow): Record<string, string> {
  const isOtherDoc = line.is_other_doc === true;
  const docRaw = strVal(line.doc);
  const docText =
    strVal(line.doc_text) ||
    (isOtherDoc ? strVal(line.other_doc ?? docRaw) : docRaw) ||
    (isOtherDoc ? strVal(line.other_doc ?? line.doc) : strVal(line.doc));
  const deedName = strVal(line.deed_name) || strVal(line.deed);
  return {
    date: strVal(line.date),
    doc: strVal(line.doc),
    doc_text: docText,
    deed: strVal(line.deed),
    deed_name: deedName,
    favour: strVal(line.favour),
    executant: strVal(line.executant),
    extent: strVal(line.extent),
    other_doc: strVal(line.other_doc ?? (line.is_other_doc ? line.doc : "")),
    remark: strVal(line.remark),
    [EC_HIERARCHY_META.LINE_INDEX]: strVal(line.line_index),
    [EC_HIERARCHY_META.RELATED]: line.related ? "true" : "false",
    [EC_HIERARCHY_META.DATE_ID]: strVal(line.date_id),
  };
}

function blockRowIdentity(row: Record<string, string>): string {
  const id = strVal(row[EC_HIERARCHY_META.BLOCK_ID]);
  if (id) return id;
  return strVal(row[EC_HIERARCHY_META.BLOCK_POS]);
}

function entryRowIdentity(row: Record<string, string>): string {
  const dateId = strVal(row[EC_HIERARCHY_META.DATE_ID]);
  if (dateId) {
    return `${strVal(row[EC_HIERARCHY_META.BLOCK_ID])}|d:${dateId}`;
  }
  return [
    strVal(row[EC_HIERARCHY_META.BLOCK_ID]),
    strVal(row[EC_HIERARCHY_META.LINE_INDEX]),
  ].join("|");
}

/** Read EC entries count from block row field tagged ec_entries, or from meta/prefill. */
export function parseEcEntriesCount(
  blockRow: Record<string, string>,
  blockGroup: ChecklistRepeatableGroupDef | undefined,
  fallback?: string | number
): number {
  const entriesField = blockGroup ? findFieldByTag(blockGroup.fields, "ec_entries") : undefined;
  const raw = entriesField
    ? strVal(blockRow[entriesField.id])
    : strVal(fallback ?? blockRow[EC_HIERARCHY_META.BLOCK_ID]);
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function entryRowsForBlock(
  sectionId: string,
  repeatableValues: Record<string, Record<string, string>[]>,
  blockId: string
): Record<string, string>[] {
  const key = repeatableEntriesKey(sectionId, EC_ENTRY_GROUP_ID);
  const rows = repeatableValues[key] || [];
  return rows.filter((r) => strVal(r[EC_HIERARCHY_META.BLOCK_ID]) === blockId);
}

export function relatedEntryRowsForBlock(
  sectionId: string,
  repeatableValues: Record<string, Record<string, string>[]>,
  blockId: string
): Record<string, string>[] {
  return entryRowsForBlock(sectionId, repeatableValues, blockId).filter(
    (r) => r[EC_HIERARCHY_META.RELATED] === "true"
  );
}

/** Runtime card title: prefer EC number, then scrutiny position. */
export function ecBlockDisplayLabel(
  blockRow: Record<string, string>,
  blockGroup: ChecklistRepeatableGroupDef | undefined,
  fallbackIndex: number
): string {
  const ecNoField = blockGroup?.fields.find((f) => fieldTag(f) === "ec_no");
  const ecNo = ecNoField ? strVal(blockRow[ecNoField.id]) : strVal(blockRow.ec_no);
  if (ecNo) return `EC — Doc No. ${ecNo}`;
  const pos = strVal(blockRow[EC_HIERARCHY_META.BLOCK_POS]);
  if (pos) return `EC (Sl.No ${pos})`;
  return `EC ${fallbackIndex + 1}`;
}

/** Resize entry rows for one EC block to match entries count; preserve existing line data. */
export function syncEcEntryRowsForBlock(
  section: ChecklistSectionDef,
  blockRow: Record<string, string>,
  entryCount: number,
  repeatableValues: Record<string, Record<string, string>[]>
): void {
  const entryGroup = ecEntryGroup(section);
  if (!entryGroup || entryCount <= 0) return;

  const blockId = strVal(blockRow[EC_HIERARCHY_META.BLOCK_ID]) || "manual";
  const key = repeatableEntriesKey(section.id, EC_ENTRY_GROUP_ID);
  const allRows = [...(repeatableValues[key] || [])];
  const others = allRows.filter((r) => strVal(r[EC_HIERARCHY_META.BLOCK_ID]) !== blockId);
  const existingForBlock = allRows.filter(
    (r) => strVal(r[EC_HIERARCHY_META.BLOCK_ID]) === blockId
  );
  const byIndex = new Map<number, Record<string, string>>();
  existingForBlock.forEach((r) => {
    const idx = parseInt(strVal(r[EC_HIERARCHY_META.LINE_INDEX]), 10);
    if (Number.isFinite(idx) && idx > 0) byIndex.set(idx, r);
  });

  const nextForBlock: Record<string, string>[] = [];
  for (let i = 1; i <= entryCount; i++) {
    const base =
      byIndex.get(i) && typeof byIndex.get(i) === "object"
        ? { ...byIndex.get(i)! }
        : emptyRepeatableRow(entryGroup.fields);
    base[EC_HIERARCHY_META.BLOCK_ID] = blockId;
    base[EC_HIERARCHY_META.LINE_INDEX] = String(i);
    if (!strVal(base[EC_HIERARCHY_META.RELATED])) {
      base[EC_HIERARCHY_META.RELATED] = "false";
    }
    nextForBlock.push(base);
  }

  repeatableValues[key] = [...others, ...nextForBlock];
}

export function setEcEntryRelated(
  section: ChecklistSectionDef,
  blockId: string,
  lineIndex: number,
  related: boolean,
  repeatableValues: Record<string, Record<string, string>[]>
): void {
  const entryGroup = ecEntryGroup(section);
  if (!entryGroup) return;
  const key = repeatableEntriesKey(section.id, EC_ENTRY_GROUP_ID);
  const rows = [...(repeatableValues[key] || [])];
  const idx = rows.findIndex(
    (r) =>
      strVal(r[EC_HIERARCHY_META.BLOCK_ID]) === blockId &&
      strVal(r[EC_HIERARCHY_META.LINE_INDEX]) === String(lineIndex)
  );
  if (idx >= 0) {
    rows[idx] = {
      ...rows[idx],
      [EC_HIERARCHY_META.RELATED]: related ? "true" : "false",
    };
  } else {
    const row = emptyRepeatableRow(entryGroup.fields);
    row[EC_HIERARCHY_META.BLOCK_ID] = blockId;
    row[EC_HIERARCHY_META.LINE_INDEX] = String(lineIndex);
    row[EC_HIERARCHY_META.RELATED] = related ? "true" : "false";
    rows.push(row);
  }
  repeatableValues[key] = rows;
}

/** Prefill EC block + entry rows from scrutiny API (`ecVerification.blocks`). */
export function applyEcVerificationPrefill(
  sections: ChecklistSectionDef[],
  repeatableValues: Record<string, Record<string, string>[]>,
  scrutinyApiData: any,
  options?: { forceEntryFields?: boolean }
): void {
  const forceEntryFields = options?.forceEntryFields === true;
  const blocks = ecBlocksFromScrutiny(scrutinyApiData);
  if (!blocks.length) return;

  sections.forEach((section) => {
    if (sectionFlowMode(section) !== "ec_verification") return;
    const blockGroup = ecBlockGroup(section);
    const entryGroup = ecEntryGroup(section);
    if (!blockGroup) return;

    const blockKey = repeatableEntriesKey(section.id, blockGroup.id);
    const existingBlocks = repeatableValues[blockKey] || [];
    const existingBlockById = new Map<string, Record<string, string>>();
    existingBlocks.forEach((r) => {
      const id = blockRowIdentity(r);
      if (id) existingBlockById.set(id, r);
    });

    const blockRows: Record<string, string>[] = [];
    blocks.forEach((source, idx) => {
      const normalized = normalizeEcBlockSource(source);
      const id = blockRowIdentity(normalized);
      const base =
        (id && existingBlockById.get(id)) ||
        (existingBlocks[idx] && typeof existingBlocks[idx] === "object"
          ? { ...existingBlocks[idx] }
          : emptyRepeatableRow(blockGroup.fields));
      Object.keys(EC_HIERARCHY_META).forEach((mk) => {
        const metaKey = EC_HIERARCHY_META[mk as keyof typeof EC_HIERARCHY_META];
        if (normalized[metaKey]) base[metaKey] = normalized[metaKey];
      });
      blockGroup.fields.forEach((f) => prefillFieldIfEmpty(base, f, normalized));
      blockRows.push(base);
    });
    repeatableValues[blockKey] = blockRows;

    if (!entryGroup) return;
    const entryKey = repeatableEntriesKey(section.id, entryGroup.id);
    const existingEntries = repeatableValues[entryKey] || [];
    const existingEntryById = new Map<string, Record<string, string>>();
    existingEntries.forEach((r) => {
      const id = entryRowIdentity(r);
      if (id) existingEntryById.set(id, r);
    });

    const entryRows: Record<string, string>[] = [];
    blocks.forEach((block) => {
      const blockId = strVal(block.ec_block_id);
      const lines = ecRelatedLinesFromBlock(block);

      lines.forEach((line) => {
        const normalized = normalizeEcLineSource(line);
        normalized[EC_HIERARCHY_META.BLOCK_ID] = blockId;
        normalized[EC_HIERARCHY_META.RELATED] = "true";
        const id = entryRowIdentity(normalized);
        const base =
          (id && existingEntryById.get(id) && typeof existingEntryById.get(id) === "object"
            ? { ...existingEntryById.get(id)! }
            : emptyRepeatableRow(entryGroup.fields));
        Object.keys(EC_HIERARCHY_META).forEach((mk) => {
          const metaKey = EC_HIERARCHY_META[mk as keyof typeof EC_HIERARCHY_META];
          if (normalized[metaKey]) base[metaKey] = normalized[metaKey];
        });
        entryGroup.fields.forEach((f) => {
          if (forceEntryFields) {
            prefillFieldFromSource(base, f, normalized);
          } else {
            prefillFieldIfEmpty(base, f, normalized);
          }
        });
        entryRows.push(base);
      });
    });
    repeatableValues[entryKey] = entryRows;
  });
}
